/**
 * Instellingen van een woning: de prijs, de verblijfsregels en de teksten die
 * bij het boeken horen.
 *
 * Dit scherm sluit een gat dat sinds stage 4 openstond. De boekingsmotor rekent
 * met een schoonmaakbedrag, een borg, een toeslag per extra gast en kortingen bij
 * langer verblijf — maar er was nergens een plek om die in te vullen. Een tarief
 * dat altijd nul is omdat er geen invoerveld voor bestaat, is geen standaardwaarde
 * maar een bedrag dat niemand heeft gekozen.
 *
 * Drie dingen die geen opmaak zijn:
 *
 * 1. **Bedragen worden in euro's getypt en in centen opgeslagen** (regel 3). De
 *    omrekening staat op één plek, in `residence-client.ts`. Wat er niet als
 *    bedrag te lezen is, wordt geweigerd en niet stilzwijgend nul.
 * 2. **De betaalinstructies staan naast de betaalmodus, niet ergens anders.** Bij
 *    `manual` int de verhuurder zelf en is dit veld het enige dat de gast te zien
 *    krijgt over hoe hij moet betalen (§17.13). Leeg laten en toch publiceren
 *    levert reserveringen op die niemand kan innen — daarom staat de waarschuwing
 *    er meteen bij.
 * 3. **Opslaan bevestigt, ook als er niets veranderde.** Een formulier dat na het
 *    opslaan hetzelfde toont als ervoor, is niet te onderscheiden van een
 *    formulier dat niets deed.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { FormEvent, ReactElement } from 'react'

import { CancellationSection } from '../components/cancellation-section'
import { CompanySection } from '../components/company-section'
import { PublicationPanel } from '../components/publication-panel'
import { fetchTenantProfile } from '../lib/tenant-client'
import type { TenantProfileState } from '../lib/tenant-client'
import { PaymentAccountsSection } from '../components/payment-accounts-section'
import { listPaymentAccounts } from '../lib/payments-client'
import type { PaymentAccount } from '../lib/payments-client'
import { listPolicies } from '../lib/cancellation-client'
import type { CancellationPolicy } from '../lib/cancellation-client'
import { useLocale, useTranslate } from '../lib/i18n-provider'
import { listResidences } from '../lib/media-client'
import type { ResidenceOption } from '../lib/media-client'
import {
  PAYMENT_MODES,
  TOURIST_TAX_MODES,
  centsFromInput,
  centsToInput,
  fetchResidence,
  permilleFromInput,
  permilleToInput,
  residenceErrorMessageKey,
  saveContent,
  updateResidence,
} from '../lib/residence-client'
import type {
  PaymentMode,
  Residence,
  ResidenceFailure,
  ResidencePatch,
  TouristTaxMode,
} from '../lib/residence-client'

type State =
  | { readonly phase: 'loading' }
  | { readonly phase: 'empty' }
  | { readonly phase: 'failed'; readonly error: ResidenceFailure }
  | { readonly phase: 'ready'; readonly residence: Residence }

/** Wat er in de invoervelden staat; alles als tekst, want zo typt iemand het. */
interface PriceForm {
  baseRate: string
  cleaningFee: string
  deposit: string
  extraGuestFee: string
  guestsIncluded: string
  maxGuests: string
  minNights: string
  maxNights: string
  weeklyDiscount: string
  monthlyDiscount: string
  paymentMode: PaymentMode
  holdExpiryHours: string
  touristTaxMode: TouristTaxMode
  touristTaxAmount: string
  touristTaxPercent: string
  touristTaxReference: string
}

function toForm(residence: Residence): PriceForm {
  const p = residence.pricing
  return {
    baseRate: centsToInput(p.baseRateCents),
    cleaningFee: centsToInput(p.cleaningFeeCents),
    deposit: centsToInput(p.depositCents),
    extraGuestFee: centsToInput(p.extraGuestFeeCents),
    guestsIncluded: String(p.guestsIncluded),
    maxGuests: String(p.maxGuests),
    minNights: String(p.minNights),
    // Leeg betekent: geen bovengrens. Dat is iets anders dan nul.
    maxNights: p.maxNights === null ? '' : String(p.maxNights),
    weeklyDiscount: permilleToInput(p.weeklyDiscountPermille),
    monthlyDiscount: permilleToInput(p.monthlyDiscountPermille),
    paymentMode: p.paymentMode,
    holdExpiryHours: String(p.holdExpiryHours),
    touristTaxMode: p.touristTaxMode,
    touristTaxAmount: centsToInput(p.touristTaxCents),
    touristTaxPercent: permilleToInput(p.touristTaxPermille),
    touristTaxReference: p.touristTaxReference,
  }
}

/** Een geheel getal binnen grenzen, of null wanneer het dat niet is. */
function wholeNumber(value: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(value.trim())) {
    return null
  }
  const parsed = Number(value.trim())
  return parsed >= min && parsed <= max ? parsed : null
}

/**
 * Het formulier naar een patch, of de sleutel van het eerste veld dat niet klopt.
 *
 * De validatie staat hier en niet alleen bij de API, om één reden: de API weigert
 * terecht een onmogelijke waarde, maar dan is het formulier al verstuurd en staat
 * er een technische melding op het scherm. Dit is geen vervanging van die controle
 * — de API blijft de grens — maar een eerdere.
 */
function toPatch(form: PriceForm): { patch: ResidencePatch } | { problem: string } {
  const baseRateCents = centsFromInput(form.baseRate)
  if (baseRateCents === null) {
    return { problem: 'dashboard.settings.problem.baseRate' }
  }
  const cleaningFeeCents = centsFromInput(form.cleaningFee)
  if (cleaningFeeCents === null) {
    return { problem: 'dashboard.settings.problem.cleaningFee' }
  }
  const depositCents = centsFromInput(form.deposit)
  if (depositCents === null) {
    return { problem: 'dashboard.settings.problem.deposit' }
  }
  const extraGuestFeeCents = centsFromInput(form.extraGuestFee)
  if (extraGuestFeeCents === null) {
    return { problem: 'dashboard.settings.problem.extraGuestFee' }
  }

  const maxGuests = wholeNumber(form.maxGuests, 1, 100)
  if (maxGuests === null) {
    return { problem: 'dashboard.settings.problem.maxGuests' }
  }
  const guestsIncluded = wholeNumber(form.guestsIncluded, 1, 100)
  if (guestsIncluded === null || guestsIncluded > maxGuests) {
    // Meer inbegrepen dan er in de woning passen, betekent dat de toeslag nooit
    // geldt. Dat is geen fout van de database maar wel een die niemand bedoelt.
    return { problem: 'dashboard.settings.problem.guestsIncluded' }
  }

  const minNights = wholeNumber(form.minNights, 1, 365)
  if (minNights === null) {
    return { problem: 'dashboard.settings.problem.minNights' }
  }
  const maxNights = form.maxNights.trim() === '' ? null : wholeNumber(form.maxNights, 1, 3650)
  if (form.maxNights.trim() !== '' && (maxNights === null || maxNights < minNights)) {
    return { problem: 'dashboard.settings.problem.maxNights' }
  }

  const weeklyDiscountPermille = permilleFromInput(form.weeklyDiscount)
  if (weeklyDiscountPermille === null) {
    return { problem: 'dashboard.settings.problem.weeklyDiscount' }
  }
  const monthlyDiscountPermille = permilleFromInput(form.monthlyDiscount)
  if (monthlyDiscountPermille === null) {
    return { problem: 'dashboard.settings.problem.monthlyDiscount' }
  }

  const holdExpiryHours = wholeNumber(form.holdExpiryHours, 1, 720)
  if (holdExpiryHours === null) {
    return { problem: 'dashboard.settings.problem.holdExpiry' }
  }

  // Alleen het veld dat bij de gekozen modus hoort, hoeft te kloppen. Het andere
  // blijft staan zoals het stond, zodat wisselen van modus en terug niets wist.
  const touristTaxCents = centsFromInput(form.touristTaxAmount) ?? 0
  if (
    form.touristTaxMode === 'per_person_night' &&
    centsFromInput(form.touristTaxAmount) === null
  ) {
    return { problem: 'dashboard.settings.problem.touristTaxAmount' }
  }
  const touristTaxPermille = permilleFromInput(form.touristTaxPercent) ?? 0
  if (
    (form.touristTaxMode === 'percentage' || form.touristTaxMode === 'area') &&
    permilleFromInput(form.touristTaxPercent) === null
  ) {
    return { problem: 'dashboard.settings.problem.touristTaxPercent' }
  }

  return {
    patch: {
      baseRateCents,
      cleaningFeeCents,
      depositCents,
      extraGuestFeeCents,
      guestsIncluded,
      maxGuests,
      minNights,
      maxNights,
      weeklyDiscountPermille,
      monthlyDiscountPermille,
      paymentMode: form.paymentMode,
      holdExpiryHours,
      touristTaxMode: form.touristTaxMode,
      touristTaxCents,
      touristTaxPermille,
      touristTaxReference: form.touristTaxReference.trim(),
    },
  }
}

function FailureNotice({ error }: { readonly error: ResidenceFailure }): ReactElement {
  const t = useTranslate()
  return (
    <div className="notice notice--error" role="alert">
      <p className="notice__title">{t(residenceErrorMessageKey(error))}</p>
      <code className="notice__detail">{error.message}</code>
    </div>
  )
}

/** Een invoerveld met label en toelichting. */
function Field({
  label,
  hint,
  value,
  onChange,
  disabled,
  suffix,
  inputMode = 'decimal',
}: {
  readonly label: string
  readonly hint?: string
  readonly value: string
  readonly onChange: (value: string) => void
  readonly disabled: boolean
  readonly suffix?: string
  readonly inputMode?: 'decimal' | 'numeric'
}): ReactElement {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <span className="field__group">
        <input
          className="field__input"
          disabled={disabled}
          inputMode={inputMode}
          onChange={(event) => {
            onChange(event.target.value)
          }}
          type="text"
          value={value}
        />
        {suffix !== undefined && <span className="field__suffix">{suffix}</span>}
      </span>
      {hint !== undefined && <span className="field__hint">{hint}</span>}
    </label>
  )
}

export function SettingsPage(): ReactElement {
  const t = useTranslate()
  const { locale } = useLocale()

  const [residences, setResidences] = useState<readonly ResidenceOption[]>([])
  const [residenceId, setResidenceId] = useState<string | null>(null)
  const [state, setState] = useState<State>({ phase: 'loading' })
  const [form, setForm] = useState<PriceForm | null>(null)
  const [houseRules, setHouseRules] = useState('')
  const [paymentInstructions, setPaymentInstructions] = useState('')
  const [policies, setPolicies] = useState<readonly CancellationPolicy[]>([])
  const [policyId, setPolicyId] = useState<string | null>(null)
  const [paymentAccounts, setPaymentAccounts] = useState<readonly PaymentAccount[]>([])
  const [canConnectPayments, setCanConnectPayments] = useState(false)
  const [paymentAccountId, setPaymentAccountId] = useState<string | null>(null)
  const [company, setCompany] = useState<TenantProfileState>({ current: null, history: [] })
  const [problem, setProblem] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [saving, setSaving] = useState(false)

  const controller = useRef<AbortController | null>(null)

  const loadResidences = useCallback(async (): Promise<void> => {
    const result = await listResidences()
    if (!result.ok) {
      setState({ phase: 'failed', error: result.error })
      return
    }
    setResidences(result.data)
    setResidenceId((current) => current ?? result.data[0]?.id ?? null)
    if (result.data.length === 0) {
      setState({ phase: 'empty' })
    }
  }, [])

  const loadResidence = useCallback(async (): Promise<void> => {
    if (residenceId === null) {
      return
    }
    controller.current?.abort()
    const next = new AbortController()
    controller.current = next

    const result = await fetchResidence(residenceId, { signal: next.signal })
    if (next.signal.aborted) {
      return
    }
    if (!result.ok) {
      if (result.error.kind !== 'aborted') {
        setState({ phase: 'failed', error: result.error })
      }
      return
    }

    setState({ phase: 'ready', residence: result.data })
    setForm(toForm(result.data))
    setPolicyId(result.data.cancellationPolicyId)
    setPaymentAccountId(result.data.paymentAccountId)

    // De teksten in de taal van de gebruiker; ontbreekt die, dan begint hij leeg.
    const content = result.data.content.find((entry) => entry.locale === locale)
    setHouseRules(content?.houseRules ?? '')
    setPaymentInstructions(content?.paymentInstructions ?? '')
  }, [residenceId, locale])

  const loadCompany = useCallback(async (): Promise<void> => {
    const result = await fetchTenantProfile()
    if (result.ok) {
      setCompany(result.data)
      return
    }
    setState({ phase: 'failed', error: result.error })
  }, [])

  const loadPaymentAccounts = useCallback(async (): Promise<void> => {
    const result = await listPaymentAccounts()
    if (result.ok) {
      setPaymentAccounts(result.data.accounts)
      setCanConnectPayments(result.data.canConnect)
      return
    }
    setState({ phase: 'failed', error: result.error })
  }, [])

  const loadPolicies = useCallback(async (): Promise<void> => {
    const result = await listPolicies()
    if (result.ok) {
      setPolicies(result.data)
      return
    }
    // Geen voorwaarden kunnen lezen is niet hetzelfde als geen voorwaarden hebben;
    // de melding komt in de gewone foutstrook terecht.
    setState({ phase: 'failed', error: result.error })
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadResidences()
  }, [loadResidences])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadResidence()
    return () => {
      controller.current?.abort()
    }
  }, [loadResidence])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadPolicies()
  }, [loadPolicies])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadPaymentAccounts()
  }, [loadPaymentAccounts])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadCompany()
  }, [loadCompany])

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (form === null || residenceId === null) {
      return
    }

    setProblem(null)
    setSaved(false)

    const outcome = toPatch(form)
    if ('problem' in outcome) {
      setProblem(outcome.problem)
      return
    }

    setSaving(true)

    // Eerst de teksten, dan de prijs. De volgorde doet ertoe: wie in één keer
    // overstapt op handmatig betalen én de betaalinstructies invult, zou bij de
    // omgekeerde volgorde worden geweigerd — de API eist die instructies op het
    // moment dat de betaalmodus verandert (§17.13), en dan staan ze er nog niet.
    const contentResult = await saveContent(residenceId, locale, {
      houseRules,
      paymentInstructions,
    })
    if (!contentResult.ok) {
      setState({ phase: 'failed', error: contentResult.error })
      setSaving(false)
      return
    }

    // De gekozen voorwaarde gaat mee met de rest van de instellingen; hem apart
    // opslaan zou betekenen dat een halve opslag een woning met de ene prijs en de
    // andere voorwaarde achterlaat.
    const priceResult = await updateResidence(residenceId, {
      ...outcome.patch,
      cancellationPolicyId: policyId,
      paymentAccountId,
    })
    setSaving(false)

    if (!priceResult.ok) {
      setState({ phase: 'failed', error: priceResult.error })
      return
    }

    setState({ phase: 'ready', residence: priceResult.data })
    setForm(toForm(priceResult.data))
    setSaved(true)
  }

  const residence = state.phase === 'ready' ? state.residence : null
  const currency = residence?.pricing.currency ?? ''
  const isManual = form?.paymentMode === 'manual'

  return (
    <section className="page">
      <h1 className="page__title">{t('dashboard.settings.title')}</h1>
      <p className="prose">{t('dashboard.settings.intro')}</p>

      {state.phase === 'loading' && (
        <p className="page__status" role="status">
          {t('dashboard.settings.loading')}
        </p>
      )}
      {state.phase === 'empty' && (
        <p className="page__status" role="status">
          {t('dashboard.settings.noResidence')}
        </p>
      )}
      {state.phase === 'failed' && <FailureNotice error={state.error} />}

      {/* Bovenaan, want dit is de vraag waarmee een verhuurder binnenkomt: staat mijn
          woning online, en zo niet, wat mankeert eraan (§17.37). */}
      {residenceId !== null && (
        <PublicationPanel busy={saving} onChanged={loadResidence} residenceId={residenceId} />
      )}

      {/* Eerst wie er verhuurt, dan pas de woning: het eerste geldt voor de hele
          account en bepaalt straks welke regelgeving op de tweede van toepassing is
          (§17.17). */}
      <CompanySection
        busy={saving}
        onChanged={loadCompany}
        onFailure={(error) => {
          setState({ phase: 'failed', error })
        }}
        state={company}
      />

      {residences.length > 1 && (
        <label className="field field--inline">
          <span className="field__label">{t('dashboard.settings.residence')}</span>
          <select
            className="field__input"
            disabled={saving}
            onChange={(event) => {
              setResidenceId(event.target.value)
            }}
            value={residenceId ?? ''}
          >
            {residences.map((option) => (
              // De naam komt uit de database en wordt niet vertaald.
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {form !== null && residence !== null && (
        <form
          onSubmit={(event) => {
            void handleSubmit(event)
          }}
        >
          <section className="page__section">
            <h2 className="page__sectionTitle">{t('dashboard.settings.price.title')}</h2>
            <p className="prose">{t('dashboard.settings.price.intro')}</p>

            <div className="field-grid">
              <Field
                disabled={saving}
                hint={t('dashboard.settings.field.baseRateHint')}
                label={t('dashboard.settings.field.baseRate')}
                onChange={(value) => {
                  setForm({ ...form, baseRate: value })
                }}
                suffix={currency}
                value={form.baseRate}
              />
              <Field
                disabled={saving}
                hint={t('dashboard.settings.field.cleaningFeeHint')}
                label={t('dashboard.settings.field.cleaningFee')}
                onChange={(value) => {
                  setForm({ ...form, cleaningFee: value })
                }}
                suffix={currency}
                value={form.cleaningFee}
              />
              <Field
                disabled={saving}
                hint={t('dashboard.settings.field.depositHint')}
                label={t('dashboard.settings.field.deposit')}
                onChange={(value) => {
                  setForm({ ...form, deposit: value })
                }}
                suffix={currency}
                value={form.deposit}
              />
              <Field
                disabled={saving}
                hint={t('dashboard.settings.field.extraGuestFeeHint')}
                label={t('dashboard.settings.field.extraGuestFee')}
                onChange={(value) => {
                  setForm({ ...form, extraGuestFee: value })
                }}
                suffix={currency}
                value={form.extraGuestFee}
              />
            </div>
          </section>

          <section className="page__section">
            <h2 className="page__sectionTitle">{t('dashboard.settings.stay.title')}</h2>

            <div className="field-grid">
              <Field
                disabled={saving}
                inputMode="numeric"
                label={t('dashboard.settings.field.maxGuests')}
                onChange={(value) => {
                  setForm({ ...form, maxGuests: value })
                }}
                value={form.maxGuests}
              />
              <Field
                disabled={saving}
                hint={t('dashboard.settings.field.guestsIncludedHint')}
                inputMode="numeric"
                label={t('dashboard.settings.field.guestsIncluded')}
                onChange={(value) => {
                  setForm({ ...form, guestsIncluded: value })
                }}
                value={form.guestsIncluded}
              />
              <Field
                disabled={saving}
                inputMode="numeric"
                label={t('dashboard.settings.field.minNights')}
                onChange={(value) => {
                  setForm({ ...form, minNights: value })
                }}
                value={form.minNights}
              />
              <Field
                disabled={saving}
                hint={t('dashboard.settings.field.maxNightsHint')}
                inputMode="numeric"
                label={t('dashboard.settings.field.maxNights')}
                onChange={(value) => {
                  setForm({ ...form, maxNights: value })
                }}
                value={form.maxNights}
              />
              <Field
                disabled={saving}
                hint={t('dashboard.settings.field.weeklyDiscountHint')}
                label={t('dashboard.settings.field.weeklyDiscount')}
                onChange={(value) => {
                  setForm({ ...form, weeklyDiscount: value })
                }}
                suffix="%"
                value={form.weeklyDiscount}
              />
              <Field
                disabled={saving}
                hint={t('dashboard.settings.field.monthlyDiscountHint')}
                label={t('dashboard.settings.field.monthlyDiscount')}
                onChange={(value) => {
                  setForm({ ...form, monthlyDiscount: value })
                }}
                suffix="%"
                value={form.monthlyDiscount}
              />
            </div>
          </section>

          <section className="page__section">
            <h2 className="page__sectionTitle">{t('dashboard.settings.payment.title')}</h2>

            <label className="field">
              <span className="field__label">{t('dashboard.settings.field.paymentMode')}</span>
              <select
                className="field__input"
                disabled={saving}
                onChange={(event) => {
                  setForm({ ...form, paymentMode: event.target.value as PaymentMode })
                }}
                value={form.paymentMode}
              >
                {PAYMENT_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {t(`dashboard.settings.paymentMode.${mode}`)}
                  </option>
                ))}
              </select>
              <span className="field__hint">
                {t(`dashboard.settings.paymentMode.${form.paymentMode}Hint`)}
              </span>
            </label>

            <Field
              disabled={saving}
              hint={t('dashboard.settings.field.holdExpiryHint')}
              inputMode="numeric"
              label={t('dashboard.settings.field.holdExpiry')}
              onChange={(value) => {
                setForm({ ...form, holdExpiryHours: value })
              }}
              suffix={t('dashboard.settings.field.hours')}
              value={form.holdExpiryHours}
            />

            {isManual && (
              <label className="field">
                <span className="field__label">
                  {t('dashboard.settings.field.paymentInstructions')}
                </span>
                <textarea
                  className="field__input field__input--tall"
                  disabled={saving}
                  maxLength={4000}
                  onChange={(event) => {
                    setPaymentInstructions(event.target.value)
                  }}
                  rows={5}
                  value={paymentInstructions}
                />
                <span className="field__hint">
                  {/* De taalnaam en niet de code: "in het nl" is geen zin. */}
                  {t('dashboard.settings.field.paymentInstructionsHint', {
                    language: t(`common.language.${locale}`),
                  })}
                </span>
              </label>
            )}

            {isManual && paymentInstructions.trim() === '' && (
              <div className="notice notice--error" role="alert">
                {/* Zonder instructies weet de gast niet hoe hij moet betalen, en dan
                    levert een gepubliceerde site reserveringen op die niemand kan
                    innen (§17.13). */}
                <p className="notice__title">{t('dashboard.settings.payment.missing')}</p>
              </div>
            )}
          </section>

          <section className="page__section">
            <h2 className="page__sectionTitle">{t('dashboard.settings.tax.title')}</h2>
            <p className="prose">{t('dashboard.settings.tax.intro')}</p>

            <label className="field">
              <span className="field__label">{t('dashboard.settings.field.touristTaxMode')}</span>
              <select
                className="field__input"
                disabled={saving}
                onChange={(event) => {
                  setForm({ ...form, touristTaxMode: event.target.value as TouristTaxMode })
                }}
                value={form.touristTaxMode}
              >
                {TOURIST_TAX_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {t(`dashboard.settings.touristTaxMode.${mode}`)}
                  </option>
                ))}
              </select>
              <span className="field__hint">
                {t(`dashboard.settings.touristTaxMode.${form.touristTaxMode}Hint`)}
              </span>
            </label>

            {form.touristTaxMode === 'per_person_night' && (
              <Field
                disabled={saving}
                hint={t('dashboard.settings.field.touristTaxAmountHint')}
                label={t('dashboard.settings.field.touristTaxAmount')}
                onChange={(value) => {
                  setForm({ ...form, touristTaxAmount: value })
                }}
                suffix={currency}
                value={form.touristTaxAmount}
              />
            )}

            {(form.touristTaxMode === 'percentage' || form.touristTaxMode === 'area') && (
              <Field
                disabled={saving}
                hint={t('dashboard.settings.field.touristTaxPercentHint')}
                label={t('dashboard.settings.field.touristTaxPercent')}
                onChange={(value) => {
                  setForm({ ...form, touristTaxPercent: value })
                }}
                suffix="%"
                value={form.touristTaxPercent}
              />
            )}

            {form.touristTaxMode !== 'none' && (
              <label className="field">
                <span className="field__label">
                  {t('dashboard.settings.field.touristTaxReference')}
                </span>
                <input
                  className="field__input"
                  disabled={saving}
                  maxLength={100}
                  onChange={(event) => {
                    setForm({ ...form, touristTaxReference: event.target.value })
                  }}
                  type="text"
                  value={form.touristTaxReference}
                />
                <span className="field__hint">
                  {t('dashboard.settings.field.touristTaxReferenceHint')}
                </span>
              </label>
            )}
          </section>

          <PaymentAccountsSection
            accounts={paymentAccounts}
            busy={saving}
            canConnect={canConnectPayments}
            onChanged={loadPaymentAccounts}
            onFailure={(error) => {
              setState({ phase: 'failed', error })
            }}
            onSelect={setPaymentAccountId}
            paymentMode={form.paymentMode}
            selectedId={paymentAccountId}
          />

          <CancellationSection
            busy={saving}
            onChanged={loadPolicies}
            onFailure={(error) => {
              setState({ phase: 'failed', error })
            }}
            onSelect={setPolicyId}
            policies={policies}
            residenceId={residence.id}
            selectedId={policyId}
          />

          <section className="page__section">
            <h2 className="page__sectionTitle">{t('dashboard.settings.rules.title')}</h2>
            <label className="field">
              <span className="field__label">{t('dashboard.settings.field.houseRules')}</span>
              <textarea
                className="field__input field__input--tall"
                disabled={saving}
                maxLength={8000}
                onChange={(event) => {
                  setHouseRules(event.target.value)
                }}
                rows={6}
                value={houseRules}
              />
              <span className="field__hint">
                {t('dashboard.settings.field.houseRulesHint', {
                  language: t(`common.language.${locale}`),
                })}
              </span>
            </label>
          </section>

          {problem !== null && (
            <p className="request-panel__problem" role="alert">
              {t(problem)}
            </p>
          )}
          {saved && (
            <p className="page__status" role="status">
              {t('dashboard.settings.saved')}
            </p>
          )}

          <div className="request-panel__actions">
            <button className="button" disabled={saving} type="submit">
              {saving ? t('dashboard.settings.saving') : t('dashboard.settings.save')}
            </button>
          </div>
        </form>
      )}
    </section>
  )
}
